import { useCallback, useMemo } from 'react';

import { kaiPartialIdPresenter, presentUser, presentUserOrgRole } from '@kaiizen/kai-presenters';
import { useScanEditingContextSlice, useUsers, useUsersByOrgMarketId, useUsersForScan } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

export type DispatchAgentOption = { value: string; label: string; roleLabel: string };

export const useDispatchAction = () => {
  const { firstScan, project, property, scanHasScopedTasks, scanMutators } = useScanEditingContextSlice([
    'firstScan',
    'project',
    'property',
    'scanHasScopedTasks',
    'scanMutators',
  ]);

  const { usersForScan, usersForScanMutators } = useUsersForScan(firstScan?.id, { projectId: project?.id });
  const orgMarketId = property?.orgMarketId ?? undefined;
  // Any org user (admin, manager, field user, etc.) can be dispatched to, not
  // just users with the fieldUser role - but only those assigned to the
  // property's own market, matching ProjectPanel.tsx's "Field User" picker at
  // project creation. getUsersByOrgMarketId gives the market-restricted set
  // of user ids; useUsers() is still needed alongside it since it's the only
  // user-list endpoint that returns each user's role at all.
  const { users: usersInMarket } = useUsersByOrgMarketId(orgMarketId);
  const { users } = useUsers();

  const agentOptions: DispatchAgentOption[] = useMemo(() => {
    const marketUserIds = new Set((usersInMarket ?? []).map(user => user.id));
    return (users ?? [])
      .filter(user => marketUserIds.has(user.id))
      .map(user => ({
        value: user.id,
        label: presentUser(user),
        roleLabel: presentUserOrgRole(user.role),
      }));
  }, [users, usersInMarket]);

  // Whoever's already assigned when the modal opens - e.g. the field agent
  // picked at project creation (source/Models/PropertyProject.ts calls
  // assignUserToScan for scan.assignedUserId at creation time, well before
  // any dispatch action happens) - is the "default" the confirm screen shows.
  const dispatchedUserIds = useMemo(() => (usersForScan ?? []).map(user => user.id), [usersForScan]);
  const isDispatched = !!firstScan && firstScan.scanLifecycleStatus !== 'created';

  const applyDispatch = useCallback(async (selectedUserIds: string[]) => {
    if (!firstScan) return;
    const scanId = firstScan.id;
    const toAdd = selectedUserIds.filter(id => !dispatchedUserIds.includes(id));
    const toRemove = dispatchedUserIds.filter(id => !selectedUserIds.includes(id));
    await Promise.all([
      ...toAdd.map(userId => usersForScanMutators.assignUserToScan(userId, scanId)),
      ...toRemove.map(userId => usersForScanMutators.removeUserFromScan(userId, scanId)),
      scanMutators.update(scanId, { scanLifecycleStatus: 'dispatched' }),
    ]);
    trackEvent(ANALYTICS_EVENTS.scanDispatched, { scanId });
  }, [firstScan, dispatchedUserIds, usersForScanMutators, scanMutators]);

  return {
    scanHasScopedTasks: !!scanHasScopedTasks,
    agentOptions,
    dispatchedUserIds,
    isDispatched,
    projectAddress: property?.address,
    projectLabel: project ? `Project #${kaiPartialIdPresenter(project.id)}` : undefined,
    applyDispatch,
  };
};
